'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Phone, Link2, DollarSign, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof Phone;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'One Number, Zero Confusion',
    icon: Phone,
    body: (
      <>
        <p className="text-base">
          When your agency connects its CRM to Voley, every client message flows through
          your <strong>single agency phone number</strong> &mdash; the same number your
          clients already know and trust across all your systems.
        </p>
        <p className="text-[15px]">
          Your clients never have to wonder which number to text &mdash;
          it&rsquo;s always the same one, no matter where a lead is in your pipeline.
          And for your team, <strong>the entire lead journey stays visible in one
          place</strong>, so nothing falls through the cracks.
        </p>
      </>
    ),
  },
  {
    heading: 'Agency Zoom + Zapier',
    icon: Link2,
    body: (
      <>
        <p className="text-base">
          We&rsquo;re finalizing a direct partnership with Vertafore to offer a native
          integration. Until then, we use <strong>Zapier</strong> as an official bridge
          to connect Agency Zoom to Voley.
        </p>
        <p className="text-[15px]">
          Zapier has an <strong>official partnership with Agency Zoom</strong>, so the
          connection is reliable and fully supported. Once linked, new leads and
          contact updates flow seamlessly between both systems.
        </p>
        <p className="text-[15px]">
          The setup takes just a few minutes &mdash; enter your Zapier API credentials
          from Agency Zoom, and Voley handles the rest.
        </p>
      </>
    ),
  },
  {
    heading: 'Pricing',
    icon: DollarSign,
    body: (
      <>
        <p className="text-base">
          Connecting your CRM is extremely useful but does adjust your plan pricing:
        </p>
        <div className="flex flex-col gap-3 pl-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">+</div>
            <span className="text-[15px]"><strong>$5 per user</strong> per month</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">+</div>
            <span className="text-[15px]"><strong>$20 added to your base plan</strong> per month</span>
          </div>
        </div>
        <p className="text-[15px] mt-3">
          This covers the Zapier bridge, real-time sync, and dedicated support for the
          integration. You can disconnect at any time from Settings if you decide
          it&rsquo;s not for you.
        </p>
      </>
    ),
  },
  {
    heading: "Let\u2019s Get Connected",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ll walk you through the full setup:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]">Click Connect on the Agency Zoom card</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]">Find your Zapier API credentials in Agency Zoom</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]">Paste them into Voley and save</span>
          </div>
        </div>
        <p className="text-[15px] mt-4">
          Voley will connect everything for you within 24 hours.
        </p>
      </>
    ),
  },
];

interface CrmConnectWelcomeModalProps {
  onComplete: () => void;
}

export function CrmConnectWelcomeModal({ onComplete }: CrmConnectWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="CRM Connection Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let\u2019s Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
